import {useLocation} from 'react-router-dom'
import useNavigation from '../../../hooks/use-navigation'
import {useCustomerId, useCustomerType} from '@salesforce/commerce-sdk-react'
import useOcapiSite from '../../../hooks/useOcapiSite'
import React, {useEffect, useRef} from 'react'
import useEffectEvent from '../../../hooks/use-effect-event'
import {HOME_HREF, SIGN_IN_HREF} from '../../../constants'
import {removeSiteLocaleFromPath} from '../../../utils/url'
import {MyStokkePageConfigProvider} from '../hooks/use-page-config'
import {Alert, Flex, Text} from '@chakra-ui/react'
import PropTypes from 'prop-types'

const MyStokkeGuardedRoute = ({routeConfig}) => {
    const location = useLocation()
    const navigate = useNavigation()
    const customerId = useCustomerId()
    const {isRegistered, customerType} = useCustomerType()
    const {myStokkeConfig, isLoaded: isMyStokkeConfigLoaded} = useOcapiSite()
    const {configId, component: Component, allowGuest} = routeConfig
    const isLoading = !isMyStokkeConfigLoaded || !customerType

    // mark if the customer was registered before
    const isLastCustomerRegisteredRef = useRef(isRegistered)
    // use effect event to not depend on location change
    const onCustomerChange = useEffectEvent((isRegistered) => {
        // if customer is registered, note it,
        if (isRegistered) {
            isLastCustomerRegisteredRef.current = true
            return
        }

        // if it was registered before, redirect to home
        if (isLastCustomerRegisteredRef.current && !isRegistered) {
            navigate(HOME_HREF)
            return
        }

        // if not registered and the page does not allow guest redirect to login
        if (!isRegistered && !allowGuest) {
            navigate(
                // login page uses `redirect_url` query param to redirect back to the original page
                `${SIGN_IN_HREF}?redirect_url=${encodeURIComponent(
                    removeSiteLocaleFromPath(location.pathname)
                )}`,
                'push',
                {directedFrom: location.pathname}
            )
            return
        }
    })
    // handle customer logout / not registered
    useEffect(() => {
        // wait for customer to initialize
        if (!customerId) {
            return
        }
        onCustomerChange(isRegistered)
    }, [customerId, isRegistered, onCustomerChange])

    // return <pre>{JSON.stringify({isLoaded, configId, myStokkeConfig}, null, 2)}</pre>

    if (isLoading) {
        return null
    }

    if (isLastCustomerRegisteredRef.current && !isRegistered) {
        // the effect will redirect to home
        return null
    }
    if (!isRegistered && !allowGuest) {
        // the effect will redirect to login
        return null
    }

    if (!configId) {
        return <Component />
    }

    if (myStokkeConfig?.[configId]) {
        return (
            <MyStokkePageConfigProvider value={myStokkeConfig[configId]}>
                <Component />
            </MyStokkePageConfigProvider>
        )
    }

    return (
        <Alert status="error" variant="solid" borderRadius="md" marginBottom={4}>
            <Flex flexDirection="column">
                <Text>
                    <span>Missing page config for &quot;</span>
                    <strong>{configId}</strong>
                    <span>&quot; in &quot;</span>
                    <strong>myStokkeConfig</strong>
                    <span>&quot; ocapi site custom preferences.</span>
                </Text>
                <details>
                    <summary>Available config</summary>
                    <pre>{JSON.stringify(myStokkeConfig, null, 2)}</pre>
                </details>
            </Flex>
        </Alert>
    )
}

MyStokkeGuardedRoute.propTypes = {
    routeConfig: PropTypes.object.isRequired
}

export default MyStokkeGuardedRoute
